import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { appUrl, openApp, playStoreUrl } from '../lib/appUrl.js';
import { fetchLiveSpaces, fetchLiveSpacesCount, formatSpaceMeta, formatSpacePriceLabel, monthlyEquivalentPrice } from '../lib/liveSpaces.js';
import './Home.css';

import noRateCardImg from '../assets/Illustration/No rate card. Just a blurry JPEG.png';
import payFirstImg from '../assets/Illustration/Pay first. Contract never..png';
import didAdRunImg from '../assets/Illustration/Did your ad even run.png';
import bbEhaanDeva from '../assets/BB/pexels-ehaan-deva-2149036462-32358148.jpg';
import bbGaborBalazs from '../assets/BB/pexels-gaborbalazs97-27864513.jpg';
import bbIntrospective from '../assets/BB/pexels-introspectivedsgn-35038788.jpg';
import bbKeremKaplan from '../assets/BB/pexels-keremkaplan-33124631.jpg';
import bbMaxedRaw from '../assets/BB/pexels-maxed-raw-527582413-27207885.jpg';
import bbPexelsUser from '../assets/BB/pexels-pexels-user-2153528950-32765072.jpg';
import bbSarthakMaji from '../assets/BB/pexels-sarthak-maji-2152979497-32567868.jpg';
import bbShuaizhiTian from '../assets/BB/pexels-shuaizhi-tian-485596-29900805.jpg';
import bbTomfisk from '../assets/BB/pexels-tomfisk-6594779.jpg';
import bbUnlime from '../assets/BB/pexels-unlime-8181296.jpg';

// Verbatim from index.html's own inline script — do not add a 'cta' or 'faq'
// entry even though a #faq section exists; the original script doesn't
// include one either.
const NAV_SECTIONS = [
  ['hero', 'light'],
  ['problem', 'light'],
  ['solution', 'light'],
  ['metrics', 'light'],
  ['formats', 'light'],
  ['testimonials', 'light'],
  ['sides', 'light'],
  ['escrow', 'dark'],
  ['blog', 'light'],
];

const MOBILE_LINKS = [
  { href: '/#formats', label: 'Formats' },
  { href: '/#sides', label: 'For advertisers & hosts' },
  { href: '/#escrow', label: 'Escrow & payments' },
  { href: '/#testimonials', label: 'User stories' },
  { href: '/blog', label: 'Insights' },
];

const STEP_DUR = 4000;
const USER_PAUSE = 10000;

// The hero search reads real listings (fetchLiveSpaces, ../lib/liveSpaces.js)
// filtered server-side to status=='live' only, then narrowed further by
// budget client-side (Firestore can't easily range-query a price that's
// quoted in three different units — see monthlyEquivalentPrice). City/budget
// here are just the fixed option lists for those two fields' UI, not listing
// data.
const HERO_CITIES = ['Mumbai', 'Delhi', 'Bengaluru', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Ahmedabad'];

const HERO_BUDGETS = [
  { key: 'any', label: 'Any budget' },
  { key: 'under-50k', label: 'Under ₹50,000/mo' },
  { key: '50k-1l', label: '₹50,000 – ₹1,00,000/mo' },
  { key: 'over-1l', label: 'Over ₹1,00,000/mo' },
];

function matchesHeroBudget(price, budgetKey) {
  if (budgetKey === 'under-50k') return price < 50000;
  if (budgetKey === '50k-1l') return price >= 50000 && price <= 100000;
  if (budgetKey === 'over-1l') return price > 100000;
  return true;
}

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Calendar grid for the hero's custom date popover — one leading `null` per
// blank cell before the 1st, then each day of the month as an ISO date
// string so a click can hand a value straight to selectDate().
function getMonthCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: firstWeekday }, () => null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, iso });
  }
  return cells;
}

const SOLUTION_STEPS = [
  { label: 'Search & filter', copy: 'Filter verified OOH sites by city, format, footfall, and budget. Real dimensions, photos, and transparent pricing — no middlemen.' },
  { label: 'Select a space', copy: 'Review site specs and check real-time availability. Send a booking request directly — space owners confirm within hours.' },
  { label: 'Pay into escrow', copy: 'Funds are held by FlipAd until your campaign runs and proof is verified. Neither side is exposed to risk.' },
  { label: 'Campaign goes live', copy: 'Upload your creative, get it approved, and monitor status from your dashboard. Both sides see the same live data.' },
  { label: 'Proof received, funds released', copy: 'Space owners submit timestamped site photos. Once verified, escrow releases payment — campaign closed and fully on record.' },
];

const FORMATS = [
  { name: 'Billboard', desc: 'Large roadside displays for maximum visibility.', tag: 'Best for highways & arterial roads', icon: 'ph-signpost', img: bbUnlime, bg: '#0F172A' },
  { name: 'Digital Screen', desc: 'Dynamic screens for flexible, high-impact ads.', tag: 'Best for malls & metro stations', icon: 'ph-monitor-play', img: bbShuaizhiTian, bg: '#170D28' },
  { name: 'Transit Panel', desc: 'Ads across buses, trains, and transit hubs.', tag: 'Best for daily commute journeys', icon: 'ph-bus', img: bbGaborBalazs, bg: '#064E3B' },
  { name: 'Unipole Hoarding', desc: 'Tall single-pole billboards for long-range visibility.', tag: 'Best for long highway approach', icon: 'ph-broadcast', img: bbKeremKaplan, bg: '#1E293B' },
  { name: 'Street Furniture', desc: 'Eye-level ads on shelters and public fixtures.', tag: 'Best for pedestrian footfall', icon: 'ph-map-pin', img: bbPexelsUser, bg: '#78350F' },
  { name: 'Retail Kiosks', desc: 'Compact ads in high-footfall retail areas.', tag: 'Best for in-mall impulse reach', icon: 'ph-storefront', img: bbEhaanDeva, bg: '#881337' },
  { name: 'Commercial Spaces', desc: 'Walls, rooftops, and storefronts for custom branding.', tag: 'Best for landmark brand visibility', icon: 'ph-buildings', img: bbMaxedRaw, bg: '#312E81' },
];

const TESTIMONIALS = [
  {
    featured: true, d: 1, type: 'adv', statIcon: 'ph-trend-down', stat: '40% below agency rates',
    quote: `"We discovered Tier-2 locations in Nashik and Nagpur we'd never have found through an agency. Same category, same format — at 40% of what we'd been paying. The map search made it obvious in minutes."`,
    initials: 'DN', name: 'Deepa Nair', role: 'Marketing Director, FMCG Brand · Mumbai', backRole: 'Marketing Director, FMCG Brand', backImg: bbTomfisk,
  },
  {
    d: 2, type: 'host', statIcon: 'ph-calendar-check', stat: 'First booking in 18 days',
    quote: `"My sites in Coimbatore were invisible to national advertisers. Within the first month on FlipAd I had an inquiry from a Bengaluru startup and a confirmed booking."`,
    initials: 'SR', name: 'Sunita Rajan', role: 'Director, RS Outdoor Media · Coimbatore', backRole: 'RS Outdoor Media · Coimbatore', backImg: bbKeremKaplan,
  },
  {
    d: 1, type: 'adv', statIcon: 'ph-lightning', stat: '4 sites confirmed same day',
    quote: `"We used to spend three days chasing vendors over WhatsApp. On FlipAd we had four locations booked across two cities in an afternoon. The escrow gave our finance team the paper trail they always asked for."`,
    initials: 'AM', name: 'Arjun Mehta', role: 'Marketing Manager, D2C Brand · Mumbai', backRole: 'Marketing Manager, D2C Brand', backImg: bbUnlime,
  },
  {
    d: 2, type: 'host', statIcon: 'ph-hand-coins', stat: 'Zero payment disputes',
    quote: `"I used to deal through three different brokers who each took a cut. Now brands find me directly and the payment clears before I send to print. Haven't chased a late payment in six months."`,
    initials: 'KI', name: 'Karthik Iyer', role: 'Founder, Karthik Outdoor · Chennai', backRole: 'Founder, Karthik Outdoor', backImg: bbMaxedRaw,
  },
  {
    d: 3, type: 'adv', statIcon: 'ph-seal-check', stat: 'Proof of play verified',
    quote: `"The verification photos were the game-changer. Our CFO finally signed off on OOH spend because there was actual timestamped proof it ran. That's a first in three years of running outdoor campaigns."`,
    initials: 'PS', name: 'Priya Sharma', role: 'Brand Lead, Fintech Startup · Bengaluru', backRole: 'Brand Lead, Fintech · Bengaluru', backImg: bbShuaizhiTian,
  },
];

const FAQS = [
  { q: 'Who can book on FlipAd?', a: `Any brand, agency, or individual looking to run OOH advertising in India can book on FlipAd. You don't need an agency relationship or a minimum spend. Create a free account and start browsing immediately.` },
  { q: 'How is pricing set?', a: 'Space owners set their own rates and these are displayed transparently on every listing. There is no hidden negotiation — what you see is what you pay. Some premium formats allow a direct message to the space owner for custom campaign requirements.' },
  { q: 'How does escrow work for advertisers?', a: `When you confirm a booking, your payment is transferred to FlipAd escrow — not to the space owner. It is held securely until the campaign period ends and the space owner submits verified proof of display. Only then is payment released. If the campaign doesn't run, you are refunded.` },
  { q: 'What is proof of display?', a: 'At the end of your campaign, the space owner uploads a timestamped photo of your creative running on their site. FlipAd reviews the photo for accuracy and date validity before releasing payment and issuing your campaign completion report.' },
  { q: 'Can I book multiple sites for one campaign?', a: 'Yes. You can add multiple sites to a campaign plan, set the same or different dates per site, and manage everything from a single campaign dashboard. Useful for city-wide or multi-city campaigns.' },
  { q: `What happens if my ad doesn't go up?`, a: 'If a space owner fails to display your creative during the confirmed campaign period, the escrow payment is returned to you in full. FlipAd investigates all disputes and space owners who fail to deliver are flagged on the platform.' },
];

const BRAND_LOGOS = ['Mamaearth', 'Zepto', 'boAt', 'Nykaa', 'CRED', 'Lenskart', 'Meesho', 'Noise', 'Pilgrim', 'Vedix', 'Slice', 'Wakefit'];

const BLOG_FILTERS = [
  { key: 'all', label: 'All articles' },
  { key: 'Advertiser Guide', label: 'Advertiser Guide' },
  { key: 'Market Insight', label: 'Market Insight' },
  { key: 'For Space Owners', label: 'For Space Owners' },
  { key: 'Data & Research', label: 'Data & Research' },
];

function ProblemCard({ img, dataD, title, desc }) {
  return (
    <div className="prob-card reveal" data-d={dataD} role="group" aria-label={`Problem ${dataD} of 3`}>
      <div className="prob-illus">
        <img src={img} alt="" className="prob-illus-img" aria-hidden="true" />
      </div>
      <div className="prob-body">
        <p className="prob-tag"><i className="ph-fill ph-warning-circle" aria-hidden="true" />Problem {dataD}</p>
        <h3 className="prob-title">{title}</h3>
        <p className="prob-desc">{desc}</p>
      </div>
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="test-flip reveal" data-d={t.d}>
      <div className="test-flip-inner">
        <div className={`test-card test-card-front${t.featured ? ' test-card-featured' : ''}`}>
          <span className={`test-stat ${t.type}`}><i className={`ph ${t.statIcon}`} aria-hidden="true" /> {t.stat}</span>
          <blockquote className="test-quote">{t.quote}</blockquote>
          <div className="test-author-row">
            <div className={`test-av ${t.type}`} aria-hidden="true">{t.initials}</div>
            <div><div className="test-name">{t.name}</div><div className="test-role">{t.role}</div></div>
            <span className={`test-type ${t.type}`}>{t.type === 'adv' ? 'Advertiser' : 'Host'}</span>
          </div>
        </div>
        <div className="test-card-back" style={{ backgroundImage: `url(${t.backImg})` }} aria-hidden="true">
          <div className="test-back-body">
            <div className="test-back-author">
              <div className={`test-av ${t.type}`}>{t.initials}</div>
              <div><div className="test-back-name">{t.name}</div><div className="test-back-role">{t.backRole}</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`so-faq-item${isOpen ? ' is-open' : ''}`}>
      <button className="so-faq-q" aria-expanded={isOpen} onClick={onToggle}>
        {item.q}
        <i className="ph ph-plus so-faq-icon" aria-hidden="true" />
      </button>
      <div className="so-faq-a"><div className="so-faq-a-inner">{item.a}</div></div>
    </div>
  );
}

export default function Home() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);

  // Skip parallax/canvas/motion effects the same way the source does — this
  // is computed once on mount and reused by every motion-driven effect below.
  const [reducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const heroRef = useRef(null);
  const skyRef = useRef(null);
  const greensRef = useRef(null);
  const canvasRef = useRef(null);
  const tiltHintRef = useRef(null);
  const metricsSectionRef = useRef(null);
  const escDiagRef = useRef(null);
  const formatsSectionRef = useRef(null);
  const fmtListRef = useRef(null);
  const fmtFollowerRef = useRef(null);
  const fillRefs = useRef([]);

  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = useRef(0);
  const timerRef = useRef(null);
  const pauseUntilRef = useRef(0);
  const startedRef = useRef(false);

  const [activeTab, setActiveTab] = useState('adv');
  const [openFaq, setOpenFaq] = useState(null);
  const blogSectionRef = useRef(null);

  const [shownFmtIdx, setShownFmtIdx] = useState(0);
  const [followerVisible, setFollowerVisible] = useState(false);
  const [openFmtIdx, setOpenFmtIdx] = useState(null);

  // Hero search: City and Start date share one dropdown-popover design (see
  // .hero-popover in Home.css) — an input/button trigger plus a panel of
  // options, portaled into document.body. .hero-inner (the hero's content
  // column) has its own `overflow: hidden`, needed to clip the parallax
  // sky/greens/billboard layers, which was ALSO clipping any dropdown that
  // tried to open below a field inside it — no z-index fixes that, since
  // overflow:hidden clips descendants regardless of stacking order. Portaling
  // out from under that ancestor is what actually makes the popovers visible.
  // Budget is plain free-text, no popover — resolved to a HERO_BUDGETS key by
  // keyword match on search (see handleHeroSearch), not picked from a list.
  const [cityQuery, setCityQuery] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [budgetQuery, setBudgetQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const today = useState(() => new Date())[0];
  const [calMonth, setCalMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const cityFieldRef = useRef(null);
  const dateFieldRef = useRef(null);
  const [popoverRect, setPopoverRect] = useState(null);

  const heroResultsRef = useRef(null);
  const [heroSearched, setHeroSearched] = useState(false);
  const [heroSearchLoading, setHeroSearchLoading] = useState(false);
  const [heroResults, setHeroResults] = useState([]);
  const [heroResultsCity, setHeroResultsCity] = useState('');
  const [heroTotalLive, setHeroTotalLive] = useState(null);

  const anyPopoverOpen = cityOpen || dateOpen;

  const openPopover = (fieldRef, setOpen) => {
    const rect = fieldRef.current.getBoundingClientRect();
    setPopoverRect({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    setCityOpen(false);
    setDateOpen(false);
    setOpen(true);
  };

  // Outside-click-to-close, same as the real app's own reference dropdown
  // (src/components/SelectInput/SelectInput.jsx) — that component never
  // closes on scroll at all, because its dropdown isn't fixed-positioned so
  // it never goes stale. Ours IS portaled/fixed-positioned (see the comment
  // above openPopover), so it still needs a scroll guard — but the first cut
  // of that guard closed on ANY scroll, including the scroll happening
  // inside the open dropdown's own option list (.hero-popover's
  // `overflow-y: auto`), which made that inner list impossible to scroll —
  // every scroll attempt inside it closed the whole dropdown instantly.
  // `scroll` doesn't bubble, so catching it at all (including from a nested
  // overflow container) requires the capture phase — the fix is checking
  // *what* scrolled: skip it when the scrolled element is the popover's own
  // list, only close for a scroll anywhere else on the page (which really
  // would leave the cached position stale).
  useEffect(() => {
    if (!anyPopoverOpen) return undefined;
    const closeAll = () => { setCityOpen(false); setDateOpen(false); };
    const onDocMouseDown = (e) => {
      const fieldRefs = [cityFieldRef, dateFieldRef];
      const insideField = fieldRefs.some((r) => r.current && r.current.contains(e.target));
      const insidePopover = e.target.closest('.hero-popover');
      if (!insideField && !insidePopover) closeAll();
    };
    const onScroll = (e) => {
      if (e.target?.closest?.('.hero-popover')) return;
      closeAll();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', closeAll);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', closeAll);
    };
  }, [anyPopoverOpen]);

  const cityMatches = HERO_CITIES.filter((c) => c.toLowerCase().includes(cityQuery.trim().toLowerCase()));

  const selectCity = (city) => {
    setCityQuery(city);
    setCityOpen(false);
  };

  const selectDate = (isoDate) => {
    setStartDate(isoDate);
    setDateOpen(false);
  };

  const displayDate = startDate
    ? new Date(`${startDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  // Search is only meaningful once at least one of the three fields carries
  // a real value — an all-empty search would just show the same unfiltered
  // sample every time, so the button stays disabled until then rather than
  // letting it fire a no-op "search".
  const hasAnyFilter = !!(cityQuery.trim() || budgetQuery.trim() || startDate);

  // Budget is free-typed with no dropdown to pick an exact label from, so
  // resolution has to be forgiving: whichever bucket's label contains what
  // was typed (case-insensitive) — "under" or "50" both land on
  // under-50k, "over" or "1,00,000" on over-1l, empty/anything unmatched
  // falls back to no filter at all.
  const handleHeroSearch = async () => {
    if (!hasAnyFilter) return;
    const city = HERO_CITIES.find((c) => c.toLowerCase() === cityQuery.trim().toLowerCase()) || '';
    const budgetTrim = budgetQuery.trim().toLowerCase();
    const budgetKey = budgetTrim
      ? HERO_BUDGETS.find((b) => b.label.toLowerCase().includes(budgetTrim))?.key || 'any'
      : 'any';

    setHeroSearchLoading(true);
    try {
      const fetched = await fetchLiveSpaces({ city });
      const matches = fetched.filter((s) => matchesHeroBudget(monthlyEquivalentPrice(s), budgetKey));
      // No fallback to a different city's listings when the searched one has
      // no live spaces — that would silently show spaces from somewhere else
      // under this city's name. A genuine miss shows the empty state instead
      // (see the render below), naming the actual city that came up empty.
      setHeroResultsCity(city);
      setHeroResults(matches.slice(0, 3));
      // The count only matters once there's something to show a count of —
      // skips a request on a miss the empty state doesn't need anyway.
      setHeroTotalLive(matches.length > 0 ? await fetchLiveSpacesCount(city) : 0);
    } catch {
      setHeroResultsCity('');
      setHeroResults([]);
      setHeroTotalLive(null);
    } finally {
      setHeroSearchLoading(false);
      setHeroSearched(true);
    }
  };

  // The results section only mounts once heroSearched flips true, so it
  // isn't there yet on the click that sets it — wait a tick for it to render,
  // then scroll smoothly down to it. Keyed on heroResults (a new array each
  // search), not just heroSearched, so a second search re-scrolls too, e.g.
  // after the user has scrolled away to read a result.
  useEffect(() => {
    if (!heroSearched) return;
    heroResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [heroSearched, heroResults]);

  /* ── Solution: auto-advance step accordion ──────────────────────────────
     Ported 1:1 from the source: steps auto-advance every STEP_DUR while the
     section is in view; clicking a step opens it and pauses auto-advance for
     USER_PAUSE, then resumes. There is no "close on click again" — clicking
     the already-open step just restarts its progress bar and the pause. */
  const goToStep = useCallback((idx) => {
    activeStepRef.current = idx;
    setActiveStep(idx);
  }, []);

  const tick = useCallback(() => {
    const wait = pauseUntilRef.current - Date.now();
    if (wait > 0) { timerRef.current = setTimeout(tick, wait); return; }
    const next = (activeStepRef.current + 1) % SOLUTION_STEPS.length;
    goToStep(next);
    timerRef.current = setTimeout(tick, STEP_DUR);
  }, [goToStep]);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    goToStep(0);
    timerRef.current = setTimeout(tick, STEP_DUR);
  }, [goToStep, tick]);

  const handleStepClick = useCallback((idx) => {
    clearTimeout(timerRef.current);
    pauseUntilRef.current = Date.now() + USER_PAUSE;
    goToStep(idx);
    timerRef.current = setTimeout(tick, STEP_DUR);
  }, [goToStep, tick]);

  useEffect(() => {
    const section = document.getElementById('solution');
    if (!section) return undefined;
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) start();
      }, { threshold: 0.15 });
      obs.observe(section);
      return () => obs.disconnect();
    }
    start();
    return undefined;
  }, [start]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Per-step progress bar: reset every fill to 0%, then animate the active
  // one to 100% over STEP_DUR via a double rAF (forces a reflow so the
  // 0%-width is committed before the transition to 100% starts).
  useEffect(() => {
    fillRefs.current.forEach((fill) => {
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = '0%';
    });
    const activeFill = fillRefs.current[activeStep];
    if (!activeFill) return undefined;
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        activeFill.style.transition = `width ${STEP_DUR}ms linear`;
        activeFill.style.width = '100%';
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [activeStep]);

  /* ── Billboard hero: fabric sheen (canvas) ──────────────────────────────
     Three overlapping light bands rendered per-scanline with sine-wave
     displacement, drawn beneath the hero content inside the billboard face. */
  useEffect(() => {
    if (reducedMotion) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const W = 160;
    const H = 100;
    const ctx = canvas.getContext('2d');
    const imgData = ctx.createImageData(W, H);
    const px = imgData.data;
    const buf = new Float32Array(W * H);
    for (let i = 0; i < W * H; i++) { px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = 255; }

    const BANDS = [
      { sp: 0.43, ph: 0.00, wa: 0.08, wf: 3.1, ws: 0.47, bw: 0.78, pk: 0.042 },
      { sp: 0.27, ph: 0.70, wa: 0.11, wf: 4.9, ws: 0.30, bw: 0.68, pk: 0.028 },
      { sp: 0.62, ph: 1.30, wa: 0.06, wf: 2.6, ws: 0.71, bw: 0.72, pk: 0.036 },
    ];

    let t0 = null;
    let rafId;

    function frame(ts) {
      if (!t0) t0 = ts;
      const t = (ts - t0) * 0.001;
      buf.fill(0);

      for (const b of BANDS) {
        const cx0 = (t * b.sp + b.ph) % 1.6 - 0.3;
        const hw = b.bw * 0.5;
        for (let yi = 0; yi < H; yi++) {
          const fy = yi / (H - 1);
          const disp = Math.sin(fy * b.wf * Math.PI + t * b.ws) * b.wa
                     + Math.sin(fy * b.wf * 1.618 * Math.PI + t * b.ws * 1.37 + 2.5) * b.wa * 0.45;
          const cx = cx0 + disp;
          const row = yi * W;
          for (let xi = 0; xi < W; xi++) {
            const d = Math.abs(xi / (W - 1) - cx) / hw;
            if (d >= 1) continue;
            buf[row + xi] += (1 - d * d * (3 - 2 * d)) * b.pk;
          }
        }
      }

      for (let i = 0; i < W * H; i++) {
        px[i * 4 + 3] = (Math.min(buf[i], 0.10) * 255) | 0;
      }
      ctx.putImageData(imgData, 0, 0);
      rafId = requestAnimationFrame(frame);
    }

    rafId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafId);
  }, [reducedMotion]);

  /* ── Billboard hero: cursor parallax (desktop) — sky fast, greens slow ── */
  useEffect(() => {
    const hero = heroRef.current;
    const sky = skyRef.current;
    const greens = greensRef.current;
    if (!hero || !sky || !greens) return undefined;
    if (reducedMotion) return undefined;
    if ('ontouchstart' in window) return undefined; // mobile uses gyroscope parallax

    let txS = 0, tyS = 0;
    let txG = 0, tyG = 0;
    let tgX = 0, tgY = 0;
    let rafId = null;

    function tickParallax() {
      txS += (tgX - txS) * 0.08;
      tyS += (tgY - tyS) * 0.08;
      txG += (tgX - txG) * 0.03;
      tyG += (tgY - tyG) * 0.03;

      sky.style.transform = `translate(${txS * 52}px,${tyS * 32}px)`;
      greens.style.transform = `translate(${txG * 14}px,${tyG * 8}px)`;

      const stillMoving = Math.abs(txS - tgX) > 4e-4 || Math.abs(tyS - tgY) > 4e-4
                        || Math.abs(txG - tgX) > 4e-4 || Math.abs(tyG - tgY) > 4e-4;
      rafId = stillMoving ? requestAnimationFrame(tickParallax) : null;
    }

    function onMouseMove(e) {
      const r = hero.getBoundingClientRect();
      tgX = (e.clientX - r.left - r.width * 0.5) / r.width;
      tgY = (e.clientY - r.top - r.height * 0.5) / r.height;
      if (!rafId) rafId = requestAnimationFrame(tickParallax);
    }

    function onMouseLeave() {
      tgX = 0; tgY = 0;
      if (!rafId) rafId = requestAnimationFrame(tickParallax);
    }

    hero.addEventListener('mousemove', onMouseMove);
    hero.addEventListener('mouseleave', onMouseLeave);
    return () => {
      hero.removeEventListener('mousemove', onMouseMove);
      hero.removeEventListener('mouseleave', onMouseLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [reducedMotion]);

  /* ── Hero: gyroscope parallax (mobile) ──────────────────────────────────
     iOS 13+: DeviceOrientationEvent.requestPermission() must be called from
     the #hero-tilt-hint click handler (unambiguous user gesture).
     Android: the listener attaches at mount; tapping the button recalibrates
     the baseline. Both: the first orientation event with non-null
     beta/gamma hides the button and sets the calibration baseline. */
  useEffect(() => {
    const sky = skyRef.current;
    const greens = greensRef.current;
    const btn = tiltHintRef.current;
    if (!sky || !greens) return undefined;
    if (reducedMotion) return undefined;
    if (!('ontouchstart' in window)) return undefined; // desktop uses cursor parallax

    let txS = 0, tyS = 0, txG = 0, tyG = 0;
    let tgX = 0, tgY = 0;
    let rafId = null;
    let baseGamma = null;
    let baseBeta = null;
    let attached = false;

    function tickTilt() {
      txS += (tgX - txS) * 0.07;
      tyS += (tgY - tyS) * 0.07;
      txG += (tgX - txG) * 0.03;
      tyG += (tgY - tyG) * 0.03;

      sky.style.transform = `translate(${txS * 52}px,${tyS * 30}px)`;
      greens.style.transform = `translate(${txG * 16}px,${tyG * 10}px)`;

      const still = Math.abs(txS - tgX) < 4e-4 && Math.abs(tyS - tgY) < 4e-4
                 && Math.abs(txG - tgX) < 4e-4 && Math.abs(tyG - tgY) < 4e-4;
      rafId = still ? null : requestAnimationFrame(tickTilt);
    }

    function handleOrientation(e) {
      if (e.gamma === null || e.beta === null) return;
      const gamma = e.gamma;
      const beta = e.beta;
      if (baseGamma === null) {
        baseGamma = gamma;
        baseBeta = beta;
        if (btn) btn.classList.add('is-hidden');
      }
      tgX = Math.max(-0.5, Math.min(0.5, (gamma - baseGamma) / 16));
      tgY = Math.max(-0.5, Math.min(0.5, (beta - baseBeta) / 16));
      if (!rafId) rafId = requestAnimationFrame(tickTilt);
    }

    function attachListener() {
      if (attached) return;
      attached = true;
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    }

    function onBtnClick() {
      if (typeof DeviceOrientationEvent !== 'undefined'
          && typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then((state) => {
            if (state === 'granted') attachListener();
            else if (btn) btn.classList.add('is-hidden');
          })
          .catch(() => { if (btn) btn.classList.add('is-hidden'); });
      } else {
        // Android: recalibrate baseline on tap
        baseGamma = null;
        baseBeta = null;
      }
    }

    if (btn) btn.addEventListener('click', onBtnClick);

    if (typeof DeviceOrientationEvent !== 'undefined'
        && typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS 13+: button stays visible, waiting for tap
    } else if (window.DeviceOrientationEvent) {
      // Android / non-iOS 12-: attach immediately, no permission needed
      if (btn) btn.classList.add('is-hidden');
      attachListener();
    } else {
      // No gyro support
      if (btn) btn.classList.add('is-hidden');
    }

    return () => {
      if (btn) btn.removeEventListener('click', onBtnClick);
      if (attached) window.removeEventListener('deviceorientation', handleOrientation);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [reducedMotion]);

  /* ── Metrics bar: counter animation ─────────────────────────────────── */
  useEffect(() => {
    const section = metricsSectionRef.current;
    if (!section) return undefined;
    const counters = section.querySelectorAll('.metric-num[data-target]');
    if (!counters.length) return undefined;

    function easeOut(t) { return 1 - (1 - t) ** 3; }
    function formatNum(n, fmt) {
      const v = Math.round(n);
      return fmt === 'comma' ? v.toLocaleString('en-US') : String(v);
    }
    function display(el, value) {
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      const fmt = el.dataset.format || '';
      el.textContent = prefix + formatNum(value, fmt) + suffix;
    }

    const timeouts = [];
    const rafs = [];
    function runCounter(el, startAt) {
      const target = parseFloat(el.dataset.target);
      const duration = 1200;
      timeouts.push(setTimeout(() => {
        const t0 = performance.now();
        function tickCounter(now) {
          const progress = Math.min((now - t0) / duration, 1);
          display(el, easeOut(progress) * target);
          if (progress < 1) rafs.push(requestAnimationFrame(tickCounter));
          else display(el, target);
        }
        rafs.push(requestAnimationFrame(tickCounter));
      }, startAt));
    }

    if (reducedMotion) {
      counters.forEach((el) => display(el, parseFloat(el.dataset.target)));
      return undefined;
    }

    let obs;
    if ('IntersectionObserver' in window) {
      let fired = false;
      obs = new IntersectionObserver((entries) => {
        if (fired || !entries[0].isIntersecting) return;
        fired = true;
        counters.forEach((el, i) => runCounter(el, i * 100));
        obs.disconnect();
      }, { threshold: 0.3 });
      obs.observe(section);
    } else {
      counters.forEach((el, i) => runCounter(el, i * 100));
    }

    return () => {
      if (obs) obs.disconnect();
      timeouts.forEach(clearTimeout);
      rafs.forEach(cancelAnimationFrame);
    };
  }, [reducedMotion]);

  /* ── Escrow diagram: trigger draw animation on scroll ─────────────────
     Toggled via direct classList mutation, not React state — see the same
     note in SpaceOwners.jsx's escrow effect: useScrollReveal also mutates
     this element's classList imperatively (adding "in"), outside React's
     render cycle, and a state-driven className here would have React's next
     re-render silently wipe that out. */
  useEffect(() => {
    const el = escDiagRef.current;
    if (!el) return undefined;
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { el.classList.add('esc-active'); obs.disconnect(); }
      }, { threshold: 0.20 });
      obs.observe(el);
      return () => obs.disconnect();
    }
    el.classList.add('esc-active');
    return undefined;
  }, []);

  /* ── Blog category filter — literal port of the original static site's own
     script (lumen-landing/index.html, "Blog category filter" IIFE): querying
     .blog-filter/.blog-featured/.blog-card directly and toggling
     'active'/'is-hidden' via classList on click, rather than deriving
     className from React state. Scoped to blogSectionRef (not `document`,
     which the original used) since this component can mount/unmount as
     client-side routes change, unlike the original's one-page-load site. */
  useEffect(() => {
    const section = blogSectionRef.current;
    if (!section) return undefined;
    const chips = section.querySelectorAll('.blog-filter');
    const featured = section.querySelector('.blog-featured');
    const cards = section.querySelectorAll('.blog-card');

    function handleChipClick() {
      const f = this.dataset.filter;
      chips.forEach((c) => c.classList.remove('active'));
      this.classList.add('active');

      if (f === 'all') {
        if (featured) featured.classList.remove('is-hidden');
        cards.forEach((c) => c.classList.remove('is-hidden'));
      } else {
        if (featured) featured.classList.toggle('is-hidden', featured.dataset.cat !== f);
        cards.forEach((c) => c.classList.toggle('is-hidden', c.dataset.cat !== f));
      }
    }

    chips.forEach((chip) => chip.addEventListener('click', handleChipClick));
    return () => chips.forEach((chip) => chip.removeEventListener('click', handleChipClick));
  }, []);

  /* ── Formats section: cursor follower + mobile expand ───────────────── */
  useEffect(() => {
    const list = fmtListRef.current;
    const follower = fmtFollowerRef.current;
    const section = formatsSectionRef.current;
    if (!list || !follower || !section) return undefined;

    const rows = Array.from(list.querySelectorAll('.fmt-row'));
    const mq = window.matchMedia('(max-width: 768px)');

    function handleMouseMove(e) {
      if (mq.matches) return;
      const fw = follower.offsetWidth;
      const fh = follower.offsetHeight;
      const left = e.clientX + 24 + fw > window.innerWidth ? e.clientX - 24 - fw : e.clientX + 24;
      follower.style.left = `${left}px`;
      follower.style.top = `${e.clientY - fh / 2}px`;
    }
    function handleListLeave() { setFollowerVisible(false); }

    const enterHandlers = rows.map((row, i) => {
      const handler = () => { if (!mq.matches) { setShownFmtIdx(i); setFollowerVisible(true); } };
      row.addEventListener('mouseenter', handler);
      return handler;
    });
    const clickHandlers = rows.map((row, i) => {
      const handler = () => {
        if (!mq.matches) return;
        setOpenFmtIdx((prev) => (prev === i ? null : i));
      };
      row.addEventListener('click', handler);
      return handler;
    });
    list.addEventListener('mouseleave', handleListLeave);
    if (!reducedMotion) section.addEventListener('mousemove', handleMouseMove);

    return () => {
      rows.forEach((row, i) => {
        row.removeEventListener('mouseenter', enterHandlers[i]);
        row.removeEventListener('click', clickHandlers[i]);
      });
      list.removeEventListener('mouseleave', handleListLeave);
      if (!reducedMotion) section.removeEventListener('mousemove', handleMouseMove);
    };
  }, [reducedMotion]);

  return (
    <>
      <Seo
        title="FlipAd — India's Out-of-Home Advertising Marketplace"
        description="Discover and book verified out-of-home advertising spaces across India, or list your own site and earn from bookings — all escrow-protected."
        path="/"
      />
      <Header mobileLinks={MOBILE_LINKS} activeNavKey={null} />

      {/* ════════════════════════════════════════════════
          HERO — CSS-built billboard
      ════════════════════════════════════════════════ */}
      <section className="hero" id="hero" aria-labelledby="hero-hl" ref={heroRef}>
        <div className="hero-sky" aria-hidden="true" ref={skyRef} />

        <div className="hero-bb-scene" id="hero-bb-scene">
          <div className="hero-bb-spacer" aria-hidden="true" />

          <div className="hero-bb-board">
            <div className="hero-bb-face">
              <canvas
                ref={canvasRef}
                width={160}
                height={100}
                aria-hidden="true"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
              />
              <div className="hero-bb-frame" aria-hidden="true" />

              <div className="hero-inner">
                <div className="hero-eyebrow" role="text">
                  <i className="ph ph-sparkle" aria-hidden="true" />
                  India&apos;s OOH Marketplace &nbsp;·&nbsp; Early Access
                </div>

                <h1 className="hero-headline" id="hero-hl">
                  Book a hoarding<br />
                  <em>like you book a flight.</em>
                </h1>

                <p className="hero-sub">
                  Search thousands of verified spaces across India. Confirm in minutes.
                  Pay safely through escrow. Know your campaign ran.
                </p>

                <div className="hero-search" role="search" aria-label="Search OOH spaces">
                  <div className="hero-search-seg" ref={cityFieldRef}>
                    <span className="hero-search-label">City</span>
                    <input
                      type="text"
                      className="hero-search-input"
                      placeholder="Mumbai, Delhi, Bengaluru…"
                      aria-label="Choose city"
                      autoComplete="off"
                      value={cityQuery}
                      onChange={(e) => { setCityQuery(e.target.value); openPopover(cityFieldRef, setCityOpen); }}
                      onFocus={() => openPopover(cityFieldRef, setCityOpen)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setCityOpen(false); }}
                    />
                  </div>
                  <span className="hero-search-div" aria-hidden="true" />
                  <div className="hero-search-seg" ref={dateFieldRef}>
                    <span className="hero-search-label">Start date</span>
                    <button
                      type="button"
                      className="hero-search-input hero-search-trigger"
                      aria-haspopup="dialog"
                      aria-expanded={dateOpen}
                      onClick={() => openPopover(dateFieldRef, setDateOpen)}
                    >
                      {displayDate || <span className="hero-search-placeholder">Pick a date</span>}
                    </button>
                  </div>
                  <span className="hero-search-div" aria-hidden="true" />
                  <div className="hero-search-seg">
                    <span className="hero-search-label">Budget</span>
                    <input
                      type="text"
                      className="hero-search-input"
                      aria-label="Set budget"
                      autoComplete="off"
                      placeholder="Any budget"
                      value={budgetQuery}
                      onChange={(e) => setBudgetQuery(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="hero-search-cta"
                    disabled={!hasAnyFilter || heroSearchLoading}
                    aria-disabled={!hasAnyFilter || heroSearchLoading}
                    onClick={handleHeroSearch}
                  >
                    <i className="ph ph-magnifying-glass" aria-hidden="true" />
                    {heroSearchLoading ? 'Searching…' : 'Search spaces'}
                  </button>
                </div>

                {cityOpen && popoverRect && createPortal(
                  <div className="hero-popover" role="listbox" style={{ top: popoverRect.top, left: popoverRect.left, width: popoverRect.width }}>
                    {cityMatches.length > 0 ? cityMatches.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className="hero-popover-option"
                        role="option"
                        aria-selected={cityQuery === c}
                        onClick={() => selectCity(c)}
                      >
                        {c}
                      </button>
                    )) : (
                      <div className="hero-popover-empty">No matching city</div>
                    )}
                  </div>,
                  document.body
                )}

                {dateOpen && popoverRect && createPortal(
                  <div className="hero-popover hero-popover-cal" role="dialog" aria-label="Choose start date" style={{ top: popoverRect.top, left: popoverRect.left, width: Math.max(popoverRect.width, 280) }}>
                    <div className="hero-cal-head">
                      <button type="button" className="hero-cal-nav" aria-label="Previous month" onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>
                        <i className="ph ph-caret-left" aria-hidden="true" />
                      </button>
                      <span className="hero-cal-label">{calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                      <button type="button" className="hero-cal-nav" aria-label="Next month" onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>
                        <i className="ph ph-caret-right" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="hero-cal-weekdays">
                      {WEEKDAY_LABELS.map((w, i) => <span key={`${w}-${i}`}>{w}</span>)}
                    </div>
                    <div className="hero-cal-grid">
                      {getMonthCells(calMonth).map((cell, i) => (
                        cell ? (
                          <button
                            key={cell.iso}
                            type="button"
                            className={`hero-cal-day${startDate === cell.iso ? ' is-selected' : ''}`}
                            onClick={() => selectDate(cell.iso)}
                          >
                            {cell.day}
                          </button>
                        ) : <span key={`blank-${i}`} className="hero-cal-day is-blank" />
                      ))}
                    </div>
                  </div>,
                  document.body
                )}

                <Link to="/space-owners" className="hero-host-link">
                  Are you a space owner? <strong>List your space →</strong>
                </Link>
              </div>
            </div>

            <div className="hero-bb-mount" aria-hidden="true" />
          </div>

          <div className="hero-bb-poles-area" aria-hidden="true">
            <div className="hero-pole hero-pole-left">
              <div className="hero-pole-shaft" />
              <div className="hero-pole-foot" />
            </div>
            <div className="hero-pole hero-pole-right">
              <div className="hero-pole-shaft" />
              <div className="hero-pole-foot" />
            </div>
          </div>
        </div>

        <div className="hero-greens" aria-hidden="true" ref={greensRef} />

        <div className="hero-scroll" aria-hidden="true">
          <span>Scroll</span>
          <i className="ph ph-caret-down" style={{ fontSize: 15 }} />
        </div>

        <button className="hero-tilt-hint" id="hero-tilt-hint" type="button" aria-label="Enable tilt parallax" ref={tiltHintRef}>
          <i className="ph ph-device-mobile-vibrate" aria-hidden="true" />
          <span>Tilt to explore</span>
        </button>
      </section>

      {/* ════════════════════════════════════════════════
          HERO SEARCH RESULTS — a normal in-flow section, deliberately NOT
          inside .hero: .hero is `position: sticky; height: 100vh;
          overflow: hidden` (needed for the parallax billboard scene), which
          silently clipped this panel whenever it grew taller than the hero's
          own fixed height instead of pushing the page down. Living here
          instead lets it take whatever height it needs, and the "Search
          spaces" handler (handleHeroSearch) scrolls to it directly since a
          sticky hero doesn't naturally reveal what comes after it just by
          existing — see the scroll effect below.
      ════════════════════════════════════════════════ */}
      {heroSearched && (
        <section className="hero-results-section" id="hero-search-results" ref={heroResultsRef} aria-label="Search results">
          <div className="container">
            <div className="hero-results reveal in" role="region" aria-label="Search results preview">
              {heroResults.length === 0 ? (
                <div className="hero-results-empty-wrap">
                  <p className="hero-results-empty">
                    {heroResultsCity
                      ? `No live spaces in ${heroResultsCity} yet — check back soon, or sign in to browse other cities.`
                      : 'No live spaces match that search yet — check back soon, or sign in to browse everything.'}
                  </p>
                  <a href={playStoreUrl()} target="_blank" rel="noopener noreferrer" className="lmn-btn primary sm">Sign in to see all spaces</a>
                </div>
              ) : (
                <>
                  <div className="hero-results-grid">
                    {heroResults.map((r) => (
                      <div className="hero-result-card" key={r.id}>
                        <div className="hero-result-thumb" style={{ backgroundImage: r.photoURL ? `url(${r.photoURL})` : undefined }} />
                        <div className="hero-result-info">
                          <div className="hero-result-name">{r.name}</div>
                          <div className="hero-result-meta">{formatSpaceMeta(r)}</div>
                          <div className="hero-result-price">{formatSpacePriceLabel(r)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hero-results-gate">
                    <span>
                      {heroTotalLive != null ? heroTotalLive.toLocaleString('en-IN') : '—'} verified space{heroTotalLive === 1 ? '' : 's'}
                      {heroResultsCity ? ` in ${heroResultsCity}` : ' across India'}
                    </span>
                    <a href={playStoreUrl()} target="_blank" rel="noopener noreferrer" className="lmn-btn primary sm">Sign in to see all spaces</a>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════
          TRUST BAND — Platform scale + brand logos
      ════════════════════════════════════════════════ */}
      <section className="trust-band" id="metrics" aria-label="Platform scale and trusted brands" ref={metricsSectionRef}>
        <div className="brand-marquee reveal">
          <p className="brand-marquee-label">Campaigns run by</p>
          <div className="brand-marquee-track">
            {BRAND_LOGOS.map((name) => <span className="brand-logo" key={`set1-${name}`}>{name}</span>)}
            {BRAND_LOGOS.map((name) => <span className="brand-logo" aria-hidden="true" key={`set2-${name}`}>{name}</span>)}
          </div>
        </div>

        <div className="trust-metrics">
          <div className="container">
            <div className="metrics-row">
              <div className="metric-block reveal" data-d="1">
                <div className="metric-icon"><i className="ph ph-map-pin" aria-hidden="true" /></div>
                <div className="metric-num" data-target="1240" data-suffix="+" data-format="comma">1,240+</div>
                <div className="metric-label">Verified spaces</div>
              </div>
              <div className="metric-div" aria-hidden="true" />
              <div className="metric-block reveal" data-d="2">
                <div className="metric-icon"><i className="ph ph-lightning" aria-hidden="true" /></div>
                <div className="metric-num" data-target="15" data-prefix={'< '} data-suffix={' mins'}>{'< 15 mins'}</div>
                <div className="metric-label">Search to booking</div>
              </div>
              <div className="metric-div" aria-hidden="true" />
              <div className="metric-block reveal" data-d="3">
                <div className="metric-icon"><i className="ph ph-shield-check" aria-hidden="true" /></div>
                <div className="metric-num" data-target="100" data-suffix="%">100%</div>
                <div className="metric-label">Escrow protected</div>
              </div>
              <div className="metric-div" aria-hidden="true" />
              <div className="metric-block reveal" data-d="4">
                <div className="metric-icon"><i className="ph ph-calendar-check" aria-hidden="true" /></div>
                <div className="metric-num" data-target="2000" data-prefix="From ₹" data-format="comma">From ₹2,000</div>
                <div className="metric-label">per day, no lock-in</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          PROBLEM — The current reality
      ════════════════════════════════════════════════ */}
      <section className="problem sec" id="problem" aria-labelledby="prob-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-10)' }}>
            <p className="eyebrow">The current reality</p>
            <h2 className="sec-title" id="prob-hl">This is how OOH works today.</h2>
            <p className="sec-desc">
              If you&apos;ve ever tried to book a hoarding in India, you already know. The process is
              informal, opaque, and surprisingly easy to get burned by — on both sides.
            </p>
          </div>

          <div className="prob-cards">
            <ProblemCard
              img={noRateCardImg}
              dataD={1}
              title="No rate card. Just a blurry JPEG."
              desc="Pricing exists only as informal messages and photographs — no dimensions, no inclusions, no formal quote you can present for finance sign-off."
            />
            <ProblemCard
              img={payFirstImg}
              dataD={2}
              title="Pay first. Contract never."
              desc={'100% advance demanded upfront with a promise that paperwork will "sort itself out." No contract, no receipt — nothing protecting either party.'}
            />
            <ProblemCard
              img={didAdRunImg}
              dataD={3}
              title="Did your ad even run?"
              desc="A blurry photo sent weeks late, impossible to date or verify. No timestamps, no geo-tags, no third-party check — this passes as proof in unorganised OOH today."
            />
          </div>
        </div>
      </section>
      {/* ════════════════════════════════════════════════
          SOLUTION — The entire flow
      ════════════════════════════════════════════════ */}
      <section className="solution sec" id="solution" aria-labelledby="sol-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 580, marginBottom: 'var(--lumen-space-12)' }}>
            <p className="eyebrow">What FlipAd does differently</p>
            <h2 className="sec-title" id="sol-hl">The entire OOH flow. In one place.</h2>
            <p className="sec-desc">Discovery, booking, payment, campaign management, and verified proof — handled end-to-end. For advertisers and space owners both.</p>
          </div>

          <div className="sol-layout reveal">
            <div className="sol-accordion" id="sol-acc">
              {SOLUTION_STEPS.map((step, i) => (
                <div className={`sol-step${activeStep === i ? ' is-active' : ''}`} key={step.label}>
                  <button className="sol-step-head" aria-expanded={activeStep === i} onClick={() => handleStepClick(i)}>
                    <span className="sol-step-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="sol-step-info">
                      <span className="sol-step-label">{step.label}</span>
                    </span>
                    <i className="ph ph-caret-down sol-step-chev" aria-hidden="true" />
                  </button>
                  <div className="sol-step-body">
                    <div><p className="sol-step-copy">{step.copy}</p></div>
                  </div>
                  <div className="sol-step-bar">
                    <div className="sol-step-bar-fill" ref={(el) => { fillRefs.current[i] = el; }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="sol-panel">
              <div className="sol-frame">
                <div className="sol-frame-bar">
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-dot" />
                  <div className="sol-frame-url">app.flipad.in</div>
                </div>
                <div className="sol-illu-wrap" id="sol-illu-wrap">

                  {/* Step 1: Search & filter */}
                  <div className={`sol-illu${activeStep === 0 ? ' is-active' : ''}`}>
                    <div className="si-search">
                      <div className="si-searchbox"><i className="ph ph-magnifying-glass" style={{ fontSize: 12 }} /> Search spaces...</div>
                      <span className="si-pill filter">Date range</span>
                      <span className="si-pill filter">Any city</span>
                      <span className="si-pill filter">Media format</span>
                    </div>
                    <div className="si-card" style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                      <div className="si-thumb"><i className="ph ph-image" /></div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                        <div className="si-name">Anna Arch Flyover, SS Hoardings</div>
                        <div className="si-sub">4th Avenue, Shanthi Colony, Anna Nagar, Chennai</div>
                        <div className="si-tags">
                          <span className="si-pill ok">New &amp; trending</span>
                          <span className="si-tag">Billboard</span>
                          <span className="si-tag">Digital</span>
                          <span className="si-tag">Landscape</span>
                        </div>
                      </div>
                    </div>
                    <div className="si-specs">
                      <div className="si-spec"><div className="si-spec-val">40×20ft</div><div className="si-spec-lbl">Spec</div></div>
                      <div className="si-spec"><div className="si-spec-val">₹28,000</div><div className="si-spec-lbl">Cost / wk</div></div>
                      <div className="si-spec"><div className="si-spec-val">1.4L/day</div><div className="si-spec-lbl">Impressions</div></div>
                      <div className="si-spec"><div className="si-spec-val">₹28.4</div><div className="si-spec-lbl">CPM</div></div>
                    </div>
                    <div className="si-list-item">
                      <div className="si-thumb si-thumb-photo" style={{ backgroundImage: `url(${bbGaborBalazs})` }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div className="si-name" style={{ fontSize: 12.5 }}>Western Express Highway, Andheri</div>
                        <div className="si-sub">Transit Panel · 30×12ft · ₹18,500/wk</div>
                      </div>
                    </div>
                    <div className="si-list-item">
                      <div className="si-thumb si-thumb-photo" style={{ backgroundImage: `url(${bbPexelsUser})` }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div className="si-name" style={{ fontSize: 12.5 }}>Powai Lake Rd, Mumbai</div>
                        <div className="si-sub">Billboard · 20×10ft · ₹42,000/mo</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Select a space */}
                  <div className={`sol-illu${activeStep === 1 ? ' is-active' : ''}`}>
                    <div className="si-photos">
                      <div className="si-photo" style={{ backgroundImage: `url(${bbEhaanDeva})`, flex: 1.4 }} />
                      <div className="si-photo" style={{ backgroundImage: `url(${bbIntrospective})` }} />
                    </div>
                    <div className="si-row">
                      <div>
                        <div className="si-name">
                          Anna Arch Flyover, SS Hoardings{' '}
                          <span style={{ color: 'var(--lumen-color-text-tertiary)', fontWeight: 600 }}>
                            · <i className="ph-fill ph-star" style={{ fontSize: 10, color: 'var(--lumen-emerald-600)' }} /> 4.8
                          </span>
                        </div>
                        <div className="si-sub">SS Hoardings Private Limited · User since Apr 2026</div>
                      </div>
                    </div>
                    <div className="si-specs">
                      <div className="si-spec"><div className="si-spec-val">40×20ft</div><div className="si-spec-lbl">Spec</div></div>
                      <div className="si-spec"><div className="si-spec-val">₹28,000</div><div className="si-spec-lbl">Cost</div></div>
                      <div className="si-spec"><div className="si-spec-val">1.4L/day</div><div className="si-spec-lbl">Imp.</div></div>
                      <div className="si-spec"><div className="si-spec-val">₹28.4</div><div className="si-spec-lbl">CPM</div></div>
                    </div>
                    <div className="si-card">
                      <div className="si-row">
                        <span className="si-pill ok"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Available</span>
                        <span className="si-sub">Jun 15 – Jun 30 · 15 days</span>
                      </div>
                      <div className="si-line"><span className="lbl">Printing</span><span className="val">FREE</span></div>
                      <div className="si-line"><span className="lbl">Creative design</span><span className="val">₹20,000</span></div>
                      <div className="si-hr" />
                      <div className="si-total"><span className="lbl">Total</span><span className="val">₹1,68,000</span></div>
                      <div className="si-btn"><i className="ph ph-plus" style={{ fontSize: 13 }} /> Add to campaign</div>
                    </div>
                  </div>

                  {/* Step 3: Pay into escrow */}
                  <div className={`sol-illu${activeStep === 2 ? ' is-active' : ''}`}>
                    <div className="si-eyebrow">Selected services</div>
                    <div className="si-card" style={{ gap: 'var(--lumen-space-3)' }}>
                      <div className="si-check-row"><i className="ph-fill ph-check-square" /> Printing <span className="val" style={{ color: 'var(--lumen-emerald-600)' }}>FREE</span></div>
                      <div className="si-check-row"><i className="ph-fill ph-check-square" /> Installation <span className="val" style={{ color: 'var(--lumen-emerald-600)' }}>FREE</span></div>
                      <div className="si-check-row off"><i className="ph ph-square" /> Creative design <span className="val">₹20,000</span></div>
                    </div>
                    <div className="si-eyebrow">Breakdown</div>
                    <div className="si-card">
                      <div className="si-line"><span className="lbl">Anna Arch Flyover, SS Hoardings · 30 days</span><span className="val">₹2,49,600</span></div>
                      <div className="si-line"><span className="lbl">9 Apr – 19 Apr · SS Hoardings Pvt. Ltd.</span></div>
                      <div className="si-hr" />
                      <div className="si-line"><span className="lbl">Subtotal · 2 spaces</span><span className="val">₹5,39,200</span></div>
                      <div className="si-line"><span className="lbl">Platform fee</span><span className="val" style={{ color: 'var(--lumen-emerald-600)' }}>Free</span></div>
                      <div className="si-line"><span className="lbl">GST (18%)</span><span className="val">₹97,056</span></div>
                      <div className="si-hr" />
                      <div className="si-total"><span className="lbl">Total payable</span><span className="val">₹6,36,256</span></div>
                    </div>
                    <div className="si-banner info"><i className="ph ph-lock" style={{ fontSize: 14, flexShrink: 0 }} /> Held in FlipAd escrow until your campaign runs and proof is verified.</div>
                    <div className="si-btn">Select campaign</div>
                  </div>

                  {/* Step 4: Campaign goes live */}
                  <div className={`sol-illu${activeStep === 3 ? ' is-active' : ''}`}>
                    <div className="si-photo-lg" style={{ backgroundImage: `url(${bbTomfisk})` }}>
                      <span className="si-pill"><i className="ph ph-circle" style={{ fontSize: 6, color: 'var(--lumen-emerald-400)' }} /> Running</span>
                    </div>
                    <div className="si-row">
                      <div>
                        <div className="si-name">Anna Arch Flyover, SS Hoardings</div>
                        <div className="si-sub"># BK-25042 · 9 Apr – 19 Apr · Host: Rajesh Kumar</div>
                      </div>
                      <span className="si-pill live"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Running</span>
                    </div>
                    <div className="si-card">
                      <div className="si-row">
                        <span className="si-name" style={{ fontSize: 12 }}><i className="ph ph-rocket-launch" style={{ color: 'var(--lumen-emerald-600)' }} />&nbsp; Campaign went live — 15 Jun 2026</span>
                        <span className="si-sub">Day 1 of 30</span>
                      </div>
                      <div className="si-progress-track"><div className="si-progress-fill" style={{ width: '3%' }} /></div>
                      <div className="si-sub">Running for 1 day · Est. 10L impressions so far</div>
                    </div>
                    <div className="si-banner info"><i className="ph ph-info" style={{ fontSize: 14, flexShrink: 0 }} /> Rajesh will upload proof of display within 48 hours of the campaign ending on 15 Jul.</div>
                  </div>

                  {/* Step 5: Proof received, funds released */}
                  <div className={`sol-illu${activeStep === 4 ? ' is-active' : ''}`}>
                    <div className="si-banner ok"><i className="ph-fill ph-check-circle" style={{ fontSize: 15, flexShrink: 0 }} /> ₹2,40,000 released to host — POD verified by you · Payment released · All done</div>
                    <div className="si-eyebrow">Proof of display</div>
                    <div className="si-photos">
                      <div className="si-photo" style={{ backgroundImage: `url(${bbMaxedRaw})` }} />
                      <div className="si-photo" style={{ backgroundImage: `url(${bbSarthakMaji})` }} />
                    </div>
                    <div className="si-card">
                      <div className="si-eyebrow">Campaign summary</div>
                      <div className="si-stats">
                        <div className="si-stat"><div className="si-stat-val">30</div><div className="si-stat-lbl">Days live</div></div>
                        <div className="si-stat"><div className="si-stat-val">35L</div><div className="si-stat-lbl">Est. impressions</div></div>
                        <div className="si-stat"><div className="si-stat-val">₹2.4L</div><div className="si-stat-lbl">Total spend</div></div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FORMATS — Every format. One platform.
      ════════════════════════════════════════════════ */}
      <section
        className="formats sec"
        id="formats"
        aria-labelledby="fmt-hl"
        ref={formatsSectionRef}
        style={reducedMotion ? { position: 'relative' } : undefined}
      >
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-12)' }}>
            <p className="eyebrow">Formats we support</p>
            <h2 className="sec-title" id="fmt-hl">Every format. One platform.</h2>
            <p className="sec-desc">From highway unipoles to metro digital screens — if it&apos;s OOH, it&apos;s on FlipAd.</p>
          </div>
        </div>

        <div className="fmt-list" id="fmt-list" role="list" ref={fmtListRef}>
          {FORMATS.map((fmt, i) => (
            <div
              key={fmt.name}
              className={`fmt-row reveal${openFmtIdx === i ? ' is-open' : ''}`}
              role="listitem"
              data-fmt-idx={i}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="container">
                <div className="fmt-row-inner">
                  <span className="fmt-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  <span className="fmt-name">{fmt.name}</span>
                  <span className="fmt-desc">{fmt.desc}</span>
                  <span className="fmt-tag"><span>{fmt.tag}</span></span>
                  <i className="ph ph-arrow-right fmt-arrow" aria-hidden="true" />
                </div>
                <div className="fmt-expand" aria-hidden="true">
                  <div className="fmt-expand-inner">
                    <div className="fmt-fimg" style={{ backgroundImage: `url(${fmt.img})`, backgroundColor: fmt.bg }}>
                      <i className={`ph ${fmt.icon}`} aria-hidden="true" />
                      <span>{fmt.name}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`fmt-follower${reducedMotion ? ' is-static' : ''}${followerVisible ? ' is-visible' : ''}`}
          id="fmt-follower"
          aria-hidden="true"
          ref={fmtFollowerRef}
        >
          <div className="fmt-follower-inner">
            {FORMATS.map((fmt, i) => (
              <div
                key={fmt.name}
                className={`fmt-fimg${shownFmtIdx === i ? ' is-shown' : ''}`}
                data-fmt={i}
                style={{ backgroundImage: `url(${fmt.img})`, backgroundColor: fmt.bg }}
              >
                <i className={`ph ${fmt.icon}`} aria-hidden="true" />
                <span>{fmt.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          TESTIMONIALS — Social proof
      ════════════════════════════════════════════════ */}
      <section className="testimonials sec" id="testimonials" aria-labelledby="test-hl">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: 520, marginLeft: 'auto', marginRight: 'auto', marginBottom: 'var(--lumen-space-12)' }}>
            <p className="eyebrow">What they say</p>
            <h2 className="sec-title" id="test-hl">From advertisers and hosts using FlipAd.</h2>
          </div>

          <div className="test-grid">
            {TESTIMONIALS.map((t) => <TestimonialCard t={t} key={t.name} />)}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          TWO SIDES, ONE PLATFORM
      ════════════════════════════════════════════════ */}
      <section className="two-sides sec" id="sides" aria-label="Built for both sides">
        <div className="container">
          <div className="sec-header center reveal" style={{ maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
            <p className="eyebrow">Two sides, one platform</p>
            <h2 className="sec-title">Built for everyone in OOH.</h2>
          </div>

          <div className="sides-tabs" role="tablist" aria-label="Platform audience">
            <button
              className={`sides-tab${activeTab === 'adv' ? ' is-active' : ''}`}
              role="tab" aria-selected={activeTab === 'adv'} aria-controls="panel-adv" id="tab-adv"
              onClick={() => setActiveTab('adv')}
            >
              <i className="ph ph-megaphone" aria-hidden="true" /> For Advertisers
            </button>
            <button
              className={`sides-tab${activeTab === 'host' ? ' is-active' : ''}`}
              role="tab" aria-selected={activeTab === 'host'} aria-controls="panel-host" id="tab-host"
              onClick={() => setActiveTab('host')}
            >
              <i className="ph ph-map-pin" aria-hidden="true" /> For Space Owners
            </button>
          </div>

          {/* Advertiser panel */}
          <div className={`sides-panel${activeTab === 'adv' ? ' is-active' : ''}`} id="panel-adv" role="tabpanel" aria-labelledby="tab-adv" hidden={activeTab !== 'adv'}>
            <div className="sides-panel-content reveal">
              <p className="sides-eyebrow adv"><i className="ph ph-megaphone" aria-hidden="true" /> &nbsp;For advertisers</p>
              <h2 className="sides-hl">Find the right space.<br />Book it today.</h2>
              <p className="sides-body">
                Verified OOH inventory across India — searchable by city, format, footfall,
                and budget. Book in minutes. Know your campaign ran.
              </p>
              <div className="sides-benefits" role="list">
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon adv" aria-hidden="true"><i className="ph ph-magnifying-glass" /></div>
                  <div>
                    <div className="sides-benefit-title">Search with precision</div>
                    <div className="sides-benefit-desc">Every listing has verified dimensions, actual photos, and transparent pricing. No guessing, no broker calls.</div>
                  </div>
                </div>
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon adv" aria-hidden="true"><i className="ph ph-calendar-check" /></div>
                  <div>
                    <div className="sides-benefit-title">Confirm same day</div>
                    <div className="sides-benefit-desc">Send a booking request and get a response — no callbacks, no WhatsApp threads. Hosts typically confirm within hours.</div>
                  </div>
                </div>
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon adv" aria-hidden="true"><i className="ph ph-seal-check" /></div>
                  <div>
                    <div className="sides-benefit-title">Proof it ran</div>
                    <div className="sides-benefit-desc">Time-stamped photos and placement logs. No more "trust me, it&apos;s been up all month."</div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lumen-space-3)', flexWrap: 'wrap' }}>
                <button type="button" className="lmn-btn primary md nav-auth-trigger" onClick={() => openApp('/inventory')}>Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" /></button>
                <Link to="/advertisers" className="lmn-btn ghost md">Learn more →</Link>
              </div>
            </div>

            <div className="sides-panel-visual" aria-hidden="true">
              <div className="mock-panel">
                <div className="mock-topbar">
                  <div className="mock-dots">
                    <div className="mock-dot mock-dot-r" />
                    <div className="mock-dot mock-dot-a" />
                    <div className="mock-dot mock-dot-g" />
                  </div>
                  <span className="mock-bar-label">FlipAd — Inventory Search</span>
                </div>
                <div className="mock-body">
                  <div className="mock-search-bar">
                    <i className="ph ph-magnifying-glass" style={{ fontSize: 13 }} />
                    <span>Mumbai &nbsp;·&nbsp; Billboard &nbsp;·&nbsp; Any dates</span>
                  </div>
                  <div className="adv-filters">
                    <span className="adv-chip"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Available</span>
                    <span className="adv-chip">Billboard</span>
                    <span className="adv-chip">Under ₹1L</span>
                    <span className="adv-chip plain"><i className="ph ph-funnel" style={{ fontSize: 9 }} /> More</span>
                  </div>
                  <div className="adv-grid">
                    <div className="adv-card">
                      <div className="adv-thumb" style={{ backgroundImage: `url(${bbEhaanDeva})` }} />
                      <div className="adv-info">
                        <div className="adv-name">Andheri West</div>
                        <div className="adv-meta">Billboard · 40×20 ft</div>
                        <div className="adv-card-tags"><span className="adv-card-tag">Available</span></div>
                        <div className="adv-price-row">
                          <span className="adv-price">₹85,000 / mo</span>
                          <i className="ph ph-caret-right" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                    <div className="adv-card selected">
                      <div className="adv-thumb" style={{ backgroundImage: `url(${bbIntrospective})` }} />
                      <div className="adv-info">
                        <div className="adv-name">Bandra Link Rd</div>
                        <div className="adv-meta">Hoarding · 60×30 ft</div>
                        <div className="adv-card-tags"><span className="adv-card-tag">Available</span></div>
                        <div className="adv-price-row">
                          <span className="adv-price">₹1,40,000 / mo</span>
                          <i className="ph ph-caret-right" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                    <div className="adv-card">
                      <div className="adv-thumb" style={{ backgroundImage: `url(${bbPexelsUser})` }} />
                      <div className="adv-info">
                        <div className="adv-name">Powai Lake Rd</div>
                        <div className="adv-meta">Billboard · 20×10 ft</div>
                        <div className="adv-card-tags"><span className="adv-card-tag">Booked</span></div>
                        <div className="adv-price-row">
                          <span className="adv-price">₹42,000 / mo</span>
                          <i className="ph ph-caret-right" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                    <div className="adv-card">
                      <div className="adv-thumb" style={{ backgroundImage: `url(${bbUnlime})` }} />
                      <div className="adv-info">
                        <div className="adv-name">Lower Parel</div>
                        <div className="adv-meta">Unipole · 25×15 ft</div>
                        <div className="adv-card-tags"><span className="adv-card-tag">Available</span></div>
                        <div className="adv-price-row">
                          <span className="adv-price">₹95,000 / mo</span>
                          <i className="ph ph-caret-right" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="adv-count">Showing 18 spaces in Mumbai</div>
                </div>
              </div>
            </div>
          </div>

          {/* Space Owner panel */}
          <div className={`sides-panel${activeTab === 'host' ? ' is-active' : ''}`} id="panel-host" role="tabpanel" aria-labelledby="tab-host" hidden={activeTab !== 'host'}>
            <div className="sides-panel-content">
              <p className="sides-eyebrow host"><i className="ph ph-map-pin" aria-hidden="true" /> &nbsp;For space owners</p>
              <h2 className="sides-hl">List once.<br />Let advertisers find you.</h2>
              <p className="sides-body">
                Your inventory is your asset. FlipAd gives it a verified presence,
                connects you with ready advertisers, and ensures you get paid —
                without chasing anyone or taking payment risk on an unknown client.
              </p>
              <div className="sides-benefits" role="list">
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon host" aria-hidden="true"><i className="ph ph-storefront" /></div>
                  <div>
                    <div className="sides-benefit-title">Discoverable by default</div>
                    <div className="sides-benefit-desc">Create a verified profile once. Advertisers searching by city, format, and budget will find you — no broker required.</div>
                  </div>
                </div>
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon host" aria-hidden="true"><i className="ph ph-calendar-blank" /></div>
                  <div>
                    <div className="sides-benefit-title">One dashboard</div>
                    <div className="sides-benefit-desc">Review requests, approve campaigns, and track what&apos;s running — without WhatsApp threads or spreadsheets.</div>
                  </div>
                </div>
                <div className="sides-benefit" role="listitem">
                  <div className="sides-benefit-icon host" aria-hidden="true"><i className="ph ph-currency-inr" /></div>
                  <div>
                    <div className="sides-benefit-title">Get paid. Reliably.</div>
                    <div className="sides-benefit-desc">Payment is in escrow before the campaign starts. Submit proof of play and funds arrive automatically.</div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lumen-space-3)', flexWrap: 'wrap' }}>
                <a
                  href={appUrl("/auth")} target="_blank" rel="noopener noreferrer" className="lmn-btn md"
                  style={{ background: 'var(--lumen-emerald-600)', borderColor: 'var(--lumen-emerald-600)', color: '#fff' }}
                >
                  List your space <i className="ph ph-arrow-right" aria-hidden="true" />
                </a>
                <Link to="/space-owners" className="lmn-btn ghost md">Learn more →</Link>
              </div>
            </div>

            <div className="sides-panel-visual" aria-hidden="true">
              <div className="mock-panel">
                <div className="mock-topbar">
                  <div className="mock-dots">
                    <div className="mock-dot mock-dot-r" />
                    <div className="mock-dot mock-dot-a" />
                    <div className="mock-dot mock-dot-g" />
                  </div>
                  <span className="mock-bar-label">FlipAd — Host Dashboard</span>
                </div>
                <div className="mock-body">
                  <div className="host-listing">
                    <div className="host-listing-photo" style={{ backgroundImage: `url(${bbTomfisk})` }}>
                      <span className="host-listing-photo-badge"><i className="ph ph-circle" style={{ fontSize: 6 }} /> Available</span>
                    </div>
                    <div className="host-listing-info">
                      <div>
                        <div className="host-listing-name">Andheri West</div>
                        <div className="host-listing-sub">Billboard · 40×20 ft · ₹85,000/mo</div>
                      </div>
                      <span className="host-listing-inq"><i className="ph ph-bell-ringing" style={{ fontSize: 10 }} /> 3 new</span>
                    </div>
                  </div>
                  <div className="host-requests-hd">
                    <span className="host-requests-label">Booking Requests</span>
                    <span className="host-requests-badge">2</span>
                  </div>
                  <div className="host-request">
                    <div className="host-req-top">
                      <span className="host-req-name">Sunrise Beverages</span>
                      <span className="host-req-amt">₹85,000</span>
                    </div>
                    <div className="host-req-sub">
                      <span className="host-req-dates">Mar 1 – 31</span>
                      <span className="host-req-pill running"><i className="ph ph-circle" style={{ fontSize: 5 }} /> Running</span>
                    </div>
                  </div>
                  <div className="host-request">
                    <div className="host-req-top">
                      <span className="host-req-name">TechConf 2026</span>
                      <span className="host-req-amt">₹1,40,000</span>
                    </div>
                    <div className="host-req-sub">
                      <span className="host-req-dates">Apr 1 – 30</span>
                      <span className="host-req-pill pending"><i className="ph ph-circle" style={{ fontSize: 5 }} /> Pending</span>
                    </div>
                  </div>
                  <div className="host-payout">
                    <span className="host-payout-label"><i className="ph ph-lock" style={{ fontSize: 11 }} /> Held in escrow</span>
                    <span className="host-payout-amt">₹85,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          ESCROW — The trust mechanism
      ════════════════════════════════════════════════ */}
      <section className="escrow sec" id="escrow" aria-labelledby="escrow-hl">
        <div className="container">
          <div className="sec-header reveal" style={{ maxWidth: 620 }}>
            <p className="eyebrow">How money moves</p>
            <h2 className="sec-title" id="escrow-hl" style={{ color: '#fff' }}>
              Neither side has to trust a stranger.
            </h2>
            <p className="sec-desc" style={{ color: 'var(--lumen-slate-400)' }}>
              FlipAd holds the accountability. Escrow is not a feature — it&apos;s the structural
              answer to the deepest trust problem in India&apos;s OOH market.
            </p>
          </div>

          <div className="esc-scroll-hint" aria-hidden="true">
            <i className="ph ph-arrows-left-right" style={{ fontSize: 14 }} />
            Scroll to see full payment flow
          </div>

          <div
            className="esc-diag reveal"
            id="esc-diag"
            ref={escDiagRef}
            role="img"
            aria-label="How FlipAd escrow works: advertiser books, payment locked, campaign runs, proof submitted, funds released"
          >
            <div className="esc-stepper">
              <div className="esc-stp esc-stp-1">
                <div className="esc-stp-icon"><i className="ph ph-user-circle" aria-hidden="true" /></div>
                <div className="esc-stp-title">Advertiser Books</div>
                <div className="esc-stp-sub">Selects space<br />&amp; dates</div>
              </div>

              <div className="esc-conn esc-conn-1 blue" aria-hidden="true">
                <div className="esc-conn-track">
                  <div className="esc-conn-line" />
                  <div className="esc-conn-tip" />
                </div>
                <div className="esc-conn-lbl">₹ PAYMENT</div>
              </div>

              <div className="esc-stp esc-stp-2 is-escrow">
                <div className="esc-stp-icon"><i className="ph ph-vault" aria-hidden="true" /></div>
                <div className="esc-stp-title">Funds Locked</div>
                <div className="esc-stp-sub">Held by FlipAd<br />Protected · Escrow</div>
              </div>

              <div className="esc-conn esc-conn-2 blue" aria-hidden="true">
                <div className="esc-conn-track">
                  <div className="esc-conn-line" />
                  <div className="esc-conn-tip" />
                </div>
                <div className="esc-conn-lbl">CAMPAIGN START</div>
              </div>

              <div className="esc-stp esc-stp-3">
                <div className="esc-stp-icon"><i className="ph ph-megaphone" aria-hidden="true" /></div>
                <div className="esc-stp-title">Campaign Runs</div>
                <div className="esc-stp-sub">Space goes live<br />Both sides active</div>
              </div>

              <div className="esc-conn esc-conn-3 blue" aria-hidden="true">
                <div className="esc-conn-track">
                  <div className="esc-conn-line" />
                  <div className="esc-conn-tip" />
                </div>
                <div className="esc-conn-lbl">PROOF</div>
              </div>

              <div className="esc-stp esc-stp-4">
                <div className="esc-stp-icon"><i className="ph ph-camera" aria-hidden="true" /></div>
                <div className="esc-stp-title">Proof Submitted</div>
                <div className="esc-stp-sub">Timestamped<br />Verified by FlipAd</div>
              </div>

              <div className="esc-conn esc-conn-4 green" aria-hidden="true">
                <div className="esc-conn-track">
                  <div className="esc-conn-line" />
                  <div className="esc-conn-tip" />
                </div>
                <div className="esc-conn-lbl">ON PROOF</div>
              </div>

              <div className="esc-stp esc-stp-5">
                <div className="esc-stp-icon"><i className="ph ph-check-circle" aria-hidden="true" /></div>
                <div className="esc-stp-title">Funds Released</div>
                <div className="esc-stp-sub">Space owner paid<br />Campaign closed</div>
              </div>
            </div>
          </div>

          <div className="escrow-guarantees">
            <div className="escrow-g adv reveal" data-d="1">
              <div className="escrow-g-icon adv" aria-hidden="true"><i className="ph ph-shield-check" /></div>
              <div className="escrow-g-title">For advertisers: protected until proof.</div>
              <div className="escrow-g-desc">
                Your payment is held securely at the time of booking. It is only released to the
                space owner after your campaign has run and verified proof of placement is submitted.
                If the campaign doesn&apos;t happen, neither does the payment.
              </div>
            </div>

            <div className="escrow-g host reveal" data-d="2">
              <div className="escrow-g-icon host" aria-hidden="true"><i className="ph ph-currency-inr" /></div>
              <div className="escrow-g-title">For space owners: paid when it delivers.</div>
              <div className="escrow-g-desc">
                Payment is already in escrow the moment the booking is confirmed — you are
                not waiting on an invoice or chasing a transfer. Submit your proof of play
                and funds arrive automatically. No disputes, no delays.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          BLOG / INSIGHTS
      ════════════════════════════════════════════════ */}
      <section className="blog sec" id="blog" aria-labelledby="blog-hl" ref={blogSectionRef}>
        <div className="container">
          <div className="blog-header-row reveal">
            <div>
              <p className="eyebrow">Industry intelligence</p>
              <h2 className="sec-title" id="blog-hl">Insights from the OOH market.</h2>
              <p className="sec-desc">Guides for advertisers placing their first campaign. Playbooks for space owners building a listing business. Data on India&apos;s OOH landscape.</p>
            </div>
            <Link to="/blog" className="lmn-btn secondary md" style={{ flexShrink: 0 }}>
              View all articles <i className="ph ph-arrow-right" aria-hidden="true" />
            </Link>
          </div>

          <div className="blog-filters reveal" role="group" aria-label="Filter articles by category">
            {BLOG_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`blog-filter${f.key === 'all' ? ' active' : ''}`}
                data-filter={f.key}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Link
            to="/blog-article"
            className="blog-featured reveal"
            data-cat="Advertiser Guide"
          >
            <div className="blog-featured-img" style={{ backgroundImage: `url(${bbShuaizhiTian})` }} role="img" aria-label="City billboards" />
            <div className="blog-featured-body">
              {/* <span
                className="blog-card-cat"
                style={{ width: 'fit-content', background: 'var(--lumen-color-action-primary-subtle)', borderColor: 'color-mix(in srgb,var(--lumen-color-action-primary) 22%,transparent)', color: 'var(--lumen-color-text-brand)' }}
              >
                Advertiser Guide
              </span> */}
              <h3 className="blog-featured-title">How to plan your first OOH campaign — from brief to billboard</h3>
              <p className="blog-featured-excerpt">Before you pick a site, answer three questions: where does your audience move through the city, when does this message need to land, and what does the creative need to do at distance? A framework built with 50+ first-time OOH advertisers.</p>
              <div className="blog-read-more">Read article <i className="ph ph-arrow-right" aria-hidden="true" style={{ fontSize: 13 }} /></div>
              <div className="blog-author">
                <div className="blog-author-av" aria-hidden="true">FR</div>
                <div>
                  <div className="blog-author-name">FlipAd Research Team</div>
                  <div className="blog-author-sub">May 28, 2026 &nbsp;·&nbsp; 8 min read</div>
                </div>
              </div>
            </div>
          </Link>

          <div className="blog-grid">
            <Link
              to="/blog-article"
              className="blog-card reveal"
              data-cat="Market Insight" data-d="1"
            >
              <div className="blog-card-thumb" style={{ backgroundImage: `url(${bbUnlime})` }}>
                <span className="blog-card-cat">Market Insight</span>
              </div>
              <div className="blog-card-body">
                <h3 className="blog-card-title">What makes a hoarding location worth the premium</h3>
                <div className="blog-card-author">
                  <div className="blog-card-author-av" aria-hidden="true">AG</div>
                  <div className="blog-card-byline">Ananya Gupta &nbsp;·&nbsp; Apr 2026 &nbsp;·&nbsp; 4 min read</div>
                </div>
              </div>
            </Link>

            <Link
              to="/blog-article"
              className="blog-card reveal"
              data-cat="For Space Owners" data-d="2"
            >
              <div className="blog-card-thumb" style={{ backgroundImage: `url(${bbMaxedRaw})` }}>
                <span className="blog-card-cat">For Space Owners</span>
              </div>
              <div className="blog-card-body">
                <h3 className="blog-card-title">Why proof of play matters — and how to ask for it</h3>
                <div className="blog-card-author">
                  <div className="blog-card-author-av" aria-hidden="true">FO</div>
                  <div className="blog-card-byline">FlipAd Ops Team &nbsp;·&nbsp; Apr 2026 &nbsp;·&nbsp; 5 min read</div>
                </div>
              </div>
            </Link>

            <Link
              to="/blog-article"
              className="blog-card reveal"
              data-cat="Data & Research" data-d="3"
            >
              <div className="blog-card-thumb" style={{ backgroundImage: `url(${bbTomfisk})` }}>
                <span className="blog-card-cat">Data &amp; Research</span>
              </div>
              <div className="blog-card-body">
                <h3 className="blog-card-title">India OOH 2026: which formats are seeing the fastest growth</h3>
                <div className="blog-card-author">
                  <div className="blog-card-author-av" aria-hidden="true">FR</div>
                  <div className="blog-card-byline">FlipAd Research Team &nbsp;·&nbsp; Mar 2026 &nbsp;·&nbsp; 7 min read</div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          FAQ
      ════════════════════════════════════════════════ */}
      <section className="so-faq sec" id="faq" aria-labelledby="faq-hl">
        <div className="container">
          <div className="faq-layout">
            <div className="faq-main">
              <div className="sec-header reveal" style={{ maxWidth: 560, marginBottom: 'var(--lumen-space-10)' }}>
                <p className="eyebrow">Common questions</p>
                <h2 className="sec-title" id="faq-hl">Everything you need to know about FlipAd.</h2>
              </div>

              <div className="so-faq-list reveal">
                {FAQS.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq((prev) => (prev === i ? null : i))}
                  />
                ))}
              </div>
            </div>

            <aside className="faq-side reveal" data-d="2">
              <div className="faq-card">
                <span className="faq-card-badge"><i className="ph-fill ph-circle" aria-hidden="true" />Avg. reply under 2 hours</span>
                <h3 className="faq-card-title">Still have a question?</h3>
                <p className="faq-card-desc">Real people, not bots. Reach out and we&apos;ll walk you through pricing, escrow, or a specific site.</p>
                <div className="faq-card-actions">
                  <a href="mailto:support@flipad.in" className="lmn-btn secondary sm"><i className="ph ph-envelope-simple" aria-hidden="true" />Email support</a>
                  <a href="#" className="lmn-btn primary sm" onClick={(e) => e.preventDefault()}><i className="ph ph-whatsapp-logo" aria-hidden="true" />Chat on WhatsApp</a>
                </div>
              </div>
              <div className="faq-trust">
                <div className="faq-trust-item"><i className="ph ph-map-pin" aria-hidden="true" /><span><strong>1,240+</strong>&nbsp;verified spaces</span></div>
                <div className="faq-trust-item"><i className="ph ph-shield-check" aria-hidden="true" /><span><strong>100%</strong>&nbsp;escrow-protected</span></div>
                <div className="faq-trust-item"><i className="ph ph-buildings" aria-hidden="true" /><span><strong>23</strong>&nbsp;cities covered</span></div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA — Two clear paths
      ════════════════════════════════════════════════ */}
      <div className="cta-split" id="cta" role="region" aria-label="Get started">
        <div className="cta-panel adv">
          <div className="cta-panel-inner reveal">
            <p className="cta-eyebrow adv">For advertisers</p>
            <h2 className="cta-hl">Find your next OOH space.</h2>
            <p className="cta-desc">Verified hoardings, billboards, transit panels, and digital screens across India. Book the one that fits.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lumen-space-3)', alignItems: 'flex-start' }}>
              <button
                type="button"
                className="lmn-btn lg nav-auth-trigger"
                style={{ background: '#fff', borderColor: '#fff', color: 'var(--b900)' }}
                onClick={() => openApp('/inventory')}
              >
                Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" />
              </button>
              <Link to="/advertisers" className="cta-more-link">Learn more about advertising on FlipAd →</Link>
            </div>
          </div>
        </div>

        <div className="cta-panel host">
          <div className="cta-panel-inner reveal" data-d="1">
            <p className="cta-eyebrow host">For space owners</p>
            <h2 className="cta-hl">Put your inventory to work.</h2>
            <p className="cta-desc">List your spaces and connect with verified advertisers who are ready to book. Get discovered. Get paid.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lumen-space-3)', alignItems: 'flex-start' }}>
              <a
                href={appUrl("/auth")} target="_blank" rel="noopener noreferrer" className="lmn-btn lg"
                style={{ background: '#fff', borderColor: '#fff', color: 'var(--lumen-slate-900)' }}
              >
                List your space <i className="ph ph-arrow-right" aria-hidden="true" />
              </a>
              <Link to="/space-owners" className="cta-more-link">Learn more about listing on FlipAd →</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer escrowHref="#" />
    </>
  );
}
